import React, { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import WarningIcon from '@mui/icons-material/Warning';
import Button from 'src/component/UI/Button';
import { confirmRecipeUpdate, cancelRecipeUpdate } from 'src/module/recipe/action';
import { getRecipeUpdateWarning } from 'src/module/recipe/selector';
import { useTranslation } from 'src/i18n';
import moment from 'moment';
import './RecipeUpdateWarningModal.scss';

export default function RecipeUpdateWarningModal () {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const warningState = useSelector(getRecipeUpdateWarning);
  const cancelButtonRef = useRef(null);

  const isOpen = warningState && typeof warningState.get === 'function' && warningState.get('open');

  const handleConfirm = useCallback(() => {
    dispatch(confirmRecipeUpdate());
  }, [dispatch]);

  const handleCancel = useCallback(() => {
    // Extract recipeId from originalAction before dispatching cancel
    const originalAction = warningState?.get('originalAction');
    let recipeId = null;

    if (originalAction) {
      if (originalAction.toJS) {
        recipeId = originalAction.toJS()?.recipeId;
      } else {
        recipeId = originalAction.recipeId;
      }
    }

    dispatch(cancelRecipeUpdate(recipeId));
  }, [dispatch, warningState]);

  // Focus management: focus the cancel button when modal opens
  useEffect(() => {
    if (isOpen && cancelButtonRef.current) {
      // Small delay to ensure modal is fully rendered
      setTimeout(() => {
        cancelButtonRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Handle Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        handleCancel();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, handleCancel]);

  if (!isOpen) {
    return null;
  }

  const pendingBatches = warningState.get('pendingBatches', []);
  const originalAction = warningState.get('originalAction');
  const actionType = warningState.get('actionType');

  const formatScheduledDate = (date) => {
    return moment(date).format('YYYY-MM-DD HH:mm');
  };

  const batches = pendingBatches?.toJS ? pendingBatches.toJS() : (pendingBatches || []);
  const batchesCount = batches.length;

  return (
    <Modal
      open={isOpen}
      onClose={handleCancel}
      aria-labelledby='recipe-warning-modal-title'
      aria-describedby='recipe-warning-modal-description'
      aria-modal='true'
      role='alertdialog'
      data-testid='recipe-warning-modal'
    >
      <Box
        className='recipe-warning-modal'
        role='document'
        tabIndex={-1}
      >
        <div className='recipe-warning-modal-header'>
          <WarningIcon
            className='recipe-warning-icon'
            aria-hidden='true'
            aria-label=''
          />
          <Typography
            id='recipe-warning-modal-title'
            variant='h5'
            component='h2'
            className='recipe-warning-title'
          >
            {t('recipes.templateUpdateWarning')}
          </Typography>
        </div>

        <div className='recipe-warning-modal-content'>

          {t('recipes.templateHasPendingBatchesWarning')}

          {batches && batchesCount > 0 && (
            <div
              className='recipe-warning-batches-list'
              role='region'
              aria-label={t('recipes.pendingBatchesList')}
            >
              <h3 className='recipe-warning-batches-title'>
                {t('recipes.pendingBatchesList')} ({batchesCount}):
              </h3>
              <ul
                className='recipe-warning-batches-items'
                aria-label={t('recipes.pendingBatchesList')}
              >
                {batches.map((batch, index) => {
                  const scheduledAt = batch.scheduled_at ? formatScheduledDate(batch.scheduled_at) : t('common.unknown');
                  const deviceCount = batch.device_count || batch.status?.number_devices || 0;
                  const createdBy = batch.first_name && batch.last_name
                    ? `${batch.first_name} ${batch.last_name}`
                    : null;

                  return (
                    <li
                      key={batch.id || index}
                      className='recipe-warning-batch-item'
                      aria-label={`${t('batchSchedules.scheduled')} ${index + 1}: ${scheduledAt}, ${deviceCount} ${t('batchSchedules.devices')}${createdBy ? `, ${t('batchStatus.createdBy')} ${createdBy}` : ''}`}
                    >
                      <div className='recipe-warning-batch-content'>
                        <div className='recipe-warning-batch-main'>
                          <span className='recipe-warning-batch-number' aria-hidden='true'>{index + 1}</span>
                          <div className='recipe-warning-batch-details'>
                            <span className='recipe-warning-batch-label'>{t('batchSchedules.scheduled')}:</span>
                            <span className='recipe-warning-batch-date'>{scheduledAt}</span>

                            {deviceCount > 0 && (
                              <>
                                <span className='recipe-warning-batch-label'>{t('batchSchedules.devices')}:</span>
                                <span className='recipe-warning-batch-value'>{deviceCount}</span>
                              </>
                            )}
                            {createdBy && (
                              <>
                                <span className='recipe-warning-batch-label'>{t('batchStatus.createdBy')}:</span>
                                <span className='recipe-warning-batch-value'>{createdBy}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>

        <div className='recipe-warning-modal-actions' role='group' aria-label={t('common.actions')}>
          <Button
            variant='outlined'
            onClick={handleCancel}
            ref={cancelButtonRef}
            aria-label={t('common.cancel')}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant='contained'
            onClick={handleConfirm}
            className='recipe-warning-confirm-button'
            cta
            aria-label={t('common.applyChanges')}
          >
            {t('common.applyChanges')}
          </Button>
        </div>
      </Box>
    </Modal>
  );
}
